import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { signIn, signOut } from "next-auth/react";
import { CartSheet } from "../CartSheet";
import { NavLink } from "./NavLink";
import { useNavContext } from "./NavContext";
import { useLocalOrders, useUpcomingRaves } from "./NavHooks";
import { TextScramble } from "../ui/text-scramble";

const MobileNavigation: React.FC = () => {
  const { isOpen, toggleMenu, session } = useNavContext();
  const { hasUpcomingRaves, upcomingRaveDetails } = useUpcomingRaves();
  const { hasOrders } = useLocalOrders();

  const menuVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.3 },
  };

  return (
    <div className="lg:hidden w-full flex justify-between items-center">
      <button
        className={`lg:hidden z-50 `}
        onClick={toggleMenu}
        aria-expanded={isOpen}
        aria-controls="mobile-menu"
        aria-label="Toggle Mobile Menu"
      >
        {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </button>
      <div className="lg:hidden z-50">
        <CartSheet />
      </div>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            id="mobile-menu"
            initial={menuVariants.initial}
            animate={menuVariants.animate}
            exit={menuVariants.exit}
            transition={menuVariants.transition}
            className="fixed inset-0 bg-background z-40 lg:hidden"
            role="menu"
          >
            <div className="flex flex-col items-center justify-center h-full space-y-8">
              <NavLink href="/products" ariaLabel="Go to Products">
                Products
              </NavLink>
              <div className="flex items-center space-x-1">
                <NavLink href="/raves" ariaLabel="View Raves">
                  Raves
                </NavLink>
                {hasUpcomingRaves && (
                  <span
                    className="h-1 w-1 rounded-full bg-[#FF0000]"
                    aria-hidden="true"
                  />
                )}
              </div>
              <NavLink href="/gallery" ariaLabel="Open Gallery">
                Gallery
              </NavLink>
              <NavLink
                href="https://drive.google.com/drive/folders/1QMP6sbYcWpHwk0JUPZcaw3TsF81NQg98"
                ariaLabel="Go to wallpapers"
              >
                Wallpapers
              </NavLink>
              {session ? (
                <>
                  <NavLink
                    href={`/users/${session.user.id}`}
                    ariaLabel="View Profile"
                  >
                    {session.user.name || "Profile"}
                  </NavLink>
                  <button
                    className="text-xs uppercase hover:underline"
                    onClick={() => signOut()}
                    aria-label="Log out"
                  >
                    <TextScramble characterSet=". ">Log out</TextScramble>
                  </button>
                </>
              ) : (
                <button
                  className="text-xs uppercase hover:underline"
                  onClick={() => signIn()}
                  aria-label="Log in"
                >
                  Login
                </button>
              )}
              <div className="flex items-center space-x-1">
                <NavLink href="/orders" ariaLabel="View Orders">
                  Orders
                </NavLink>
                {hasOrders && (
                  <span
                    className="h-1 w-1 rounded-full bg-[#FF0000]"
                    aria-hidden="true"
                  />
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

MobileNavigation.displayName = "MobileNavigation";
export default MobileNavigation;
